import { FADE_IN_ANIMATION } from '#app/components/Motion/constants.js';
import { animated } from '#app/components/Primitives/Animated.js';
import { Text } from '#app/components/Primitives/Text.js';
import { MODAL_SIZES } from '../constants.js';
import { ModalBaseProps, type ModalSize } from '../types.js';
import { css, styled } from 'styled-components';

const getModalMaxWidth = (size: ModalSize) => {
  let maxWidth = 'initial';

  switch (size) {
    case 'fullscreen':
    case 'fullWidth': {
      break;
    }

    default: {
      maxWidth = `${MODAL_SIZES[size]}px`;
      break;
    }
  }

  return css`
    max-width: ${maxWidth};
  `;
};

export const BetaTag = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colorsV2.brandWatermelon};
    color: ${theme.colorsV2.white};
    font-weight: ${theme.fontWeights.semiBold};
    font-size: 11px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 6px;
    border-radius: 100px;
    text-transform: uppercase;
    width: 47px;
    height: 24px;
    margin-top: 2px;
  `}
`;

export const OutsideOfModalContainer = styled.div`
  position: fixed;
  display: flex;
  width: auto;
  justify-content: center;
  align-items: center;
  left: 25%;
  right: 25%;
`;

export const Backdrop = styled.div<{
  blurBackdrop?: boolean;
  customZIndex?: number;
}>`
  ${({ blurBackdrop = false, customZIndex, theme }) => css`
    position: fixed;
    z-index: ${customZIndex ?? theme.zIndices.modal.backdrop};
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    overflow-x: hidden;
    overflow-y: auto;
    backdrop-filter: ${blurBackdrop ? 'blur(8px)' : 'none'};
  `}
`;

export const BackdropBackground = styled.div<{
  $customOpacity?: number;
  customZIndex?: number;
}>`
  ${({ $customOpacity, customZIndex, theme }) => css`
    position: fixed;
    z-index: ${customZIndex ?? theme.zIndices.modal.backdrop};
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(
      0,
      0,
      0,
      ${typeof $customOpacity === 'number' ? $customOpacity : 0.4}
    );
    overflow-x: hidden;
    overflow-y: auto;
    animation: ${FADE_IN_ANIMATION};
  `}
`;

export const DialogContainer = styled(animated.div)<{
  $size?: ModalSize;
}>`
  ${({ $size, theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 100%;
    padding: 16px;

    ${theme.mediaQueries.md} {
      padding: 32px;
    }

    ${$size === 'fullWidth'
      ? css`
          padding: 32px 0 0 !important;
          max-height: 100vh;
          @supports (max-height: 100dvh) {
            max-height: 100dvh;
          }
        `
      : undefined}

    ${$size === 'fullscreen'
      ? css`
          max-height: 100vh;
          @supports (max-height: 100dvh) {
            max-height: 100dvh;
          }
          padding: 0;

          ${theme.mediaQueries.md} {
            padding: 0;
          }
        `
      : undefined}
  `}
`;

// NOTE: `size` would conflict with DOM attribute so we called the prop `$size` here.
export const Dialog = styled(animated.div)<{
  $overrideBoxShadow?: ModalBaseProps['overrideBoxShadow'];
  $size: ModalSize;
  customZIndex?: number;
  overflowX?: 'auto' | 'hidden' | 'unset';
  overrideBackgroundColor?: ModalBaseProps['overrideBackgroundColor'];
  overrideBackgroundImageUrl?: ModalBaseProps['overrideBackgroundImageUrl'];
}>`
  ${({
    $overrideBoxShadow,
    $size,
    customZIndex,
    overflowX = 'hidden',
    overrideBackgroundColor = 'white',
    overrideBackgroundImageUrl,
    theme,
  }) => css`
    position: relative;
    z-index: ${customZIndex ? customZIndex + 1 : theme.zIndices.modal.dialog};
    overflow-x: ${overflowX};
    scrollbar-width: none;

    ${$overrideBoxShadow
      ? css`
          box-shadow: ${$overrideBoxShadow === 'none'
            ? 'none'
            : theme.boxShadowV2[$overrideBoxShadow]};
        `
      : css`
          box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.04);
        `}

    ${overrideBackgroundColor
      ? css`
          background-color: ${theme.colorsV2[overrideBackgroundColor]};
        `
      : css`
          background-color: none;
        `}

    background-image: url(${overrideBackgroundImageUrl});
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;

    &::-webkit-scrollbar {
      display: none;
    }

    ${$size === 'fullscreen'
      ? css`
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          // this ensures we keep the modal at 100% height of the viewport. The children elements will do the overflow scrolling.
          overflow: hidden;
          max-height: 100vh;
          // this dvh is 74% compatible according to caniuse. Use regular vh as a fallback.
          // dvh solves the issue of the bottom or top browser bars being included in the 100vh on mobile devices.
          @supports (max-height: 100dvh) {
            max-height: 100dvh;
          }
        `
      : css`
          border-radius: ${$size === 'fullWidth' ? '20px 20px 0 0' : '20px'};
          margin: auto 0;
        `}

    width: 100%;
    ${getModalMaxWidth($size)}
  `}
`;

/**
 * The styled component for `<Modal.Header>`.
 */
export const Header = styled.header<{
  $flex: boolean;
  $marginForCloseButton: boolean;
}>`
  ${({ $flex, $marginForCloseButton, theme }) => css`
    margin: 24px;
    margin-bottom: 0;

    ${$flex
      ? css`
          display: flex;
        `
      : undefined}

    ${$marginForCloseButton
      ? css`
          ${theme.mediaQueries.lg} {
            margin-right: 56px;
          }
        `
      : undefined}
  `}
`;

/**
 * The styled component for `<Modal.Body>`.
 */
export const Body = styled.div<{
  $size: ModalSize;
  noMargin?: boolean;
  noMarginTop?: boolean;
  textAlign?: 'center' | 'left' | 'right';
}>`
  ${({ $size, noMargin = false, noMarginTop = false, textAlign }) => css`
    padding-bottom: env(safe-area-inset-bottom, 0);
    text-wrap: pretty;
    margin: ${noMargin ? '0' : '24px'};
    ${$size === 'fullscreen'
      ? css`
          display: flex;
          flex-direction: column;
          // this ensures we keep the modal at 100% height of the viewport. The children elements will do the overflow scrolling.
          overflow: hidden;
        `
      : undefined}

    ${noMarginTop
      ? css`
          margin-top: 0;
        `
      : ''};

    ${textAlign
      ? css`
          text-align: ${textAlign};
        `
      : undefined}

    // Adjust top padding if preceding sibling is Modal.Header.
    ${Header} + & {
      margin-top: 12px;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  `}
`;

/**
 * The styled component for `<Modal.Title>`.
 */
export const Title = styled.h1<{
  justifyContent?: 'center' | 'flex-end' | 'flex-start';
}>`
  ${({ justifyContent = 'flex-start' }) => css`
    display: flex;
    gap: 8px;
    align-items: flex-start;
    justify-content: ${justifyContent};
    ${justifyContent === 'center'
      ? css`
          text-align: center;
        `
      : null}

    > p {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  `}
`;

export const AdditionalDetails = styled.div`
  margin-top: 24px;
`;

export const Subheader = styled(Text)<{
  textAlign?: 'center' | 'left' | 'right';
}>`
  ${({ textAlign = 'left', theme }) => css`
    // when text align is centered, the full bleed
    // subtext is too wide for a text element when 100% width
    // so we limit it to 85% width to avoid creating padding issues
    ${textAlign === 'center' &&
    css`
      margin-left: auto;
      margin-right: auto;
      max-width: 85%;
    `}
    margin-bottom: 24px;
    color: ${theme.colorsV2.gray70};
    text-align: ${textAlign};
    margin-top: 8px;

    ${theme.mediaQueries.md} {
      margin-top: unset;
    }
  `}
`;

export const Actions = styled.div<{
  fullWidth?: boolean;
  justifyContent?: 'center' | 'flex-end' | 'space-between';
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 24px;
  gap: 8px;

  position: relative;
  z-index: 0;

  ${({ justifyContent }) => css`
    justify-content: ${justifyContent ?? 'flex-end'};
  `};

  ${({ fullWidth }) =>
    fullWidth
      ? css`
          justify-content: stretch;
          flex-direction: column-reverse;

          > * {
            width: 100%;
          }

          > * {
            width: 100%;
          }
        `
      : undefined};
`;

export const Hero = styled.div`
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  overflow: hidden;
`;

export const IconContainer = styled.div`
  flex-shrink: 0;
`;

export const HeightTransitionContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;

    // this ensures for our mobile fullscreen modals, there is an overflow scroll.
    // We don't want this on desktop as this breaks the height transitioning logic.
    height: 100%;
    overflow-y: auto;

    ${theme.mediaQueries.lg} {
      height: unset;
      overflow-y: unset;
    }
  `}
`;

export const ProgressWrapper = styled.div`
  ${({ theme }) => css`
    height: 20px;
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
    overflow: hidden;

    ${theme.mediaQueries.lg} {
      border-top-left-radius: 20px;
      border-top-right-radius: 20px;
    }
  `}
`;

export const ProgressBar = styled.div<{ percent?: number }>`
  ${({ percent, theme }) => css`
    width: 100%;
    background: ${theme.colorsV2.gray30};
    height: 6px;
    position: relative;

    &::after {
      content: '';
      background: ${theme.gradients.primary};
      border-radius: 0px 20px 20px 0px;
      height: 6px;
      inset: 0;
      position: absolute;
      transition: width 0.3s ease-in-out;
      width: ${percent}%;
    }
  `}
`;
